import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { ButtonContainer, ContentContainer, FadeContainer, GradientText, MessageContainer } from './GuestPreferenceMessage.styles';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { TranslateFunction } from '@locales/generated';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';

interface GuestPreferenceMessageProps {
  isMobile?: boolean;
  messageKeys: { titleKey: string; subtitleKey: string }[];
  containerHeight: string;
  initialMessage?: string;
  page: string;
}

function useTypingText(fullText: string, delay = 30, startTyping = false) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!startTyping) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    let currentIndex = 0;
    let timeoutId: NodeJS.Timeout;

    function typeNextChar() {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
        timeoutId = setTimeout(typeNextChar, delay);
      } else {
        setIsComplete(true);
      }
    }

    setDisplayedText('');
    setIsComplete(false);
    typeNextChar();

    return () => clearTimeout(timeoutId);
  }, [fullText, delay, startTyping]);

  return { text: displayedText, isComplete };
}

export const GuestPreferenceMessage: React.FC<GuestPreferenceMessageProps> = ({ isMobile = false, messageKeys, containerHeight, initialMessage, page }) => {
  const { t } = useTranslation('sharedComponents');
  const { facts, nextTip } = t('guestPreferenceMessage');
  const telemetry = useAdminRegistryTelemetry();

  const [showInitialMessage, setShowInitialMessage] = useState(true);
  const [showMessages, setShowMessages] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [availableIndices, setAvailableIndices] = useState<number[]>([]);

  useEffect(() => {
    setShowInitialMessage(true);

    const initialTimer = setTimeout(() => {
      setShowInitialMessage(false);

      const messagesTimer = setTimeout(() => {
        setShowMessages(true);
        setShowButton(true);
        const indices = Array.from({ length: messageKeys.length }, (_, i) => i);
        for (let i = indices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        setAvailableIndices(indices.slice(1));
        setCurrentMessageIndex(indices[0]);
      }, 1500);

      return () => clearTimeout(messagesTimer);
    }, 10000);

    return () => clearTimeout(initialTimer);
  }, [messageKeys.length]);

  const getRandomMessage = useCallback(() => {
    if (availableIndices.length === 0) {
      const newIndices = Array.from({ length: messageKeys.length }, (_, i) => i).filter(i => i !== currentMessageIndex);
      for (let i = newIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newIndices[i], newIndices[j]] = [newIndices[j], newIndices[i]];
      }
      setAvailableIndices(newIndices.slice(1));
      setCurrentMessageIndex(newIndices[0]);
    } else {
      const randomIndex = Math.floor(Math.random() * availableIndices.length);
      const nextMessageIndex = availableIndices[randomIndex];
      setCurrentMessageIndex(nextMessageIndex);
      setAvailableIndices(prev => prev.filter((_, i) => i !== randomIndex));
    }
  }, [availableIndices, currentMessageIndex, messageKeys.length]);

  useEffect(() => {
    if (!showMessages) return;

    const intervalId = setInterval(() => {
      getRandomMessage();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [showMessages, getRandomMessage]);

  const currentMessage = useMemo(() => {
    const { titleKey, subtitleKey } = messageKeys[currentMessageIndex];
    const keyedMessages = facts as Record<string, TranslateFunction>;
    return {
      title: keyedMessages[titleKey](),
      subtitle: keyedMessages[subtitleKey](),
    };
  }, [currentMessageIndex, messageKeys, facts]);

  const { text: titleText, isComplete: isTitleComplete } = useTypingText(currentMessage.title, 15, showMessages);
  const { text: subtitleText, isComplete: isSubtitleComplete } = useTypingText(currentMessage.subtitle, 15, showMessages && isTitleComplete);

  const showNextTipButton = showButton && isTitleComplete && isSubtitleComplete;

  const handleNextTipClick = () => {
    telemetry.nextTipClick(page);
    getRandomMessage();
  };

  return (
    <MessageContainer height={containerHeight}>
      <FadeContainer
        show={showInitialMessage}
        height={containerHeight}
      >
        <GradientText
          typographyVariant="hed6"
          justifyContent="center"
          alignItems="center"
          __css={{
            textAlign: 'center',
            maxWidth: '100%',
            whiteSpace: 'pre-wrap',
          }}
        >
          {initialMessage}
        </GradientText>
      </FadeContainer>
      <FadeContainer
        show={showMessages}
        height={containerHeight}
      >
        <ContentContainer alignItems={isMobile ? 'center' : 'flex-start'}>
          <GradientText
            typographyVariant="hed3"
            __css={{
              textAlign: isMobile ? 'center' : 'left',
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
            }}
          >
            {titleText}
          </GradientText>

          <GradientText
            typographyVariant="body3"
            lineHeight={pxToRem(20)}
            __css={{
              textAlign: isMobile ? 'center' : 'left',
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
            }}
          >
            {subtitleText}
          </GradientText>
        </ContentContainer>
      </FadeContainer>
      {showButton && (
        <ButtonContainer
          isMobile={isMobile}
          show={showNextTipButton}
        >
          <ButtonV2
            variant="ghost"
            padding={0}
            height="fit-content"
            color="mono10"
            onClick={handleNextTipClick}
            typographyVariant="label2"
          >
            {nextTip()}
          </ButtonV2>
        </ButtonContainer>
      )}
    </MessageContainer>
  );
};
